import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { qsParse, qsStringify } from './query-string';
import { filterStringsToEnumValues } from './typescript';

export type Strategy = 'push' | 'replace';
let newQueryParams: {
    [key: string]: string | string[] | null | undefined;
} = {};
let newQueryParamsStrategy: Strategy = 'replace';
let isNavigationScheduled = false;

function applyNewQueryParams(
    navigate: ReturnType<typeof useNavigate>,
    location: ReturnType<typeof useLocation>,
) {
    if (isNavigationScheduled) {
        // Another navigation is already scheduled, just accumulate the params
        return;
    }

    if (Object.keys(newQueryParams).length === 0) {
        return;
    }

    // Schedule navigation to allow multiple calls to batch together
    isNavigationScheduled = true;
    Promise.resolve().then(() => {
        if (Object.keys(newQueryParams).length === 0) {
            isNavigationScheduled = false;
            return;
        }

        const pathname = location.pathname;
        const nextQuery = qsStringify({
            ...qsParse(location.search),
            ...newQueryParams,
        });
        const search = nextQuery ? `?${nextQuery}` : '';

        switch (newQueryParamsStrategy) {
            case 'push':
                navigate({ pathname, search });
                break;
            case 'replace':
                navigate({ pathname, search }, { replace: true });
                break;
        }

        newQueryParams = {};
        newQueryParamsStrategy = 'replace';
        isNavigationScheduled = false;
    });
}

export function usePersistURLInSessionStorage() {
    const navigate = useNavigate();
    const { pathname: key, search } = useLocation();
    const [isInitialized, setIsInitialized] = useState(false);
    const hasRestoredFromSessionRef = useRef<string | null>(null);

    // Update session storage when the search changes
    useEffect(() => {
        const keyValue = key.length && key[key.length - 1] === '/' ? key.slice(0, -1) : key;
        if (!isInitialized) {
            setIsInitialized(true);
            return;
        }
        sessionStorage.setItem(keyValue, search);
    }, [search, key, isInitialized]);

    // Update the URL from session storage when the page loads - per pathname
    useEffect(() => {
        const keyValue = key.length && key[key.length - 1] === '/' ? key.slice(0, -1) : key;

        // Only restore once per pathname to avoid interfering with tab navigation
        if (hasRestoredFromSessionRef.current === keyValue) {
            return;
        }

        hasRestoredFromSessionRef.current = keyValue;

        // Only restore if there's no current search params (fresh navigation)
        if (!search) {
            const value = sessionStorage.getItem(keyValue);
            if (value) {
                navigate(
                    {
                        search: value,
                    },
                    { replace: true },
                );
            }
        }
    }, [key, search, navigate]);
}

// Overload for string parameters (backward compatibility)
export function useQueryParam(
    key: string,
    defaultValue?: string | null,
): [string | null, (newValue: string | null, strategy?: Strategy) => void];

// Overload for typed parameters (use-query-params compatibility)
export function useQueryParam<T>(
    key: string,
    paramConfig: QueryParamConfig<T>,
): [T, (newValue: T, strategy?: Strategy) => void];

// Implementation
export function useQueryParam<T>(
    key: string,
    paramConfigOrDefault?: QueryParamConfig<T> | string | null,
): [T | string | null, (newValue: T | string | null, strategy?: Strategy) => void] {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = qsParse(location.search);

    // Use refs to avoid including location in dependencies
    const navigateRef = useRef(navigate);
    const locationRef = useRef(location);
    navigateRef.current = navigate;
    locationRef.current = location;

    // Determine if we're using typed params or simple string params
    const isTypedParam =
        paramConfigOrDefault != null &&
        typeof paramConfigOrDefault === 'object' &&
        'encode' in paramConfigOrDefault;
    const paramConfig = isTypedParam ? (paramConfigOrDefault as QueryParamConfig<T>) : null;
    const defaultValue = !isTypedParam ? (paramConfigOrDefault as string | null) : null;

    const setValue = (newValue: T | string | null, strategy: Strategy = 'replace') => {
        let encodedValue: string | string[] | null | undefined;
        if (paramConfig) {
            const encoded = paramConfig.encode(newValue as T);
            // Convert encoded value to proper type
            if (Array.isArray(encoded)) {
                encodedValue = encoded.filter((v): v is string => v != null);
            } else {
                encodedValue = encoded || undefined;
            }
        } else {
            encodedValue = (newValue as string | null) === null ? undefined : (newValue as string);
        }

        newQueryParams[key] = encodedValue;
        if (strategy === 'push') {
            newQueryParamsStrategy = strategy;
        }
        applyNewQueryParams(navigateRef.current, locationRef.current);
    };

    const rawValue = queryParams[key];

    if (paramConfig) {
        // Use typed parameter config
        const value = paramConfig.decode(rawValue);
        return [value, setValue];
    } else {
        // Use simple string logic
        if (rawValue === undefined) {
            return [defaultValue || null, setValue];
        }
        const value = Array.isArray(rawValue) ? rawValue[0] : rawValue || null;
        return [value, setValue];
    }
}

export function useQueryArrayParam(
    key: string,
    defaultValue: string[] = [],
): [string[], (newValue: string[], strategy?: Strategy) => void] {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = qsParse(location.search);

    // Use refs to avoid dependencies on navigate/location
    const navigateRef = useRef(navigate);
    const locationRef = useRef(location);
    navigateRef.current = navigate;
    locationRef.current = location;

    const setValue = (newValue: string[], strategy: Strategy = 'push') => {
        newQueryParams[key] = newValue;
        if (strategy === 'push') {
            newQueryParamsStrategy = strategy;
        }
        applyNewQueryParams(navigateRef.current, locationRef.current);
    };

    if (queryParams[key] === undefined) {
        return [defaultValue, setValue];
    }

    const rawValue = queryParams[key];
    let value: string[];
    if (Array.isArray(rawValue)) {
        value = rawValue.filter((v): v is string => v !== null);
    } else if (rawValue) {
        value = [rawValue];
    } else {
        value = [];
    }
    return [value, setValue];
}

export function useQueryEnumArrayParam<TEnum extends string>(
    key: string,
    enumType: Record<any, TEnum>,
    defaultValue: string[] = [],
): [TEnum[], (newValue: TEnum[], strategy?: Strategy) => void] {
    const [rawValue, setValue] = useQueryArrayParam(key, defaultValue);
    return [filterStringsToEnumValues(rawValue, enumType), setValue];
}

// Parameter type definitions (compatible with use-query-params)
export interface QueryParamConfig<T> {
    encode: (value: T) => string | (string | null)[] | null | undefined;
    decode: (value: string | (string | null)[] | null | undefined) => T;
}

// String parameter handler
export const StringParam: QueryParamConfig<string | null | undefined> = {
    encode: (value) => value || undefined,
    decode: (value) => {
        if (Array.isArray(value)) {
            return value[0] || undefined;
        }
        return value || undefined;
    },
};

// Array parameter handler
export const ArrayParam: QueryParamConfig<string[]> = {
    encode: (value) => (value.length ? value : undefined),
    decode: (value) => {
        if (Array.isArray(value)) {
            return value.filter((v): v is string => v != null);
        }
        if (value) {
            return [value];
        }
        return [];
    },
};

// Boolean parameter handler
export const BooleanParam: QueryParamConfig<boolean | null> = {
    encode: (value) => {
        if (value === true) return '1';
        if (value === false) return '0';
        return undefined;
    },
    decode: (value) => {
        if (Array.isArray(value)) {
            value = value[0];
        }
        if (value === '1' || value === 'true') return true;
        if (value === '0' || value === 'false') return false;
        return null;
    },
};

// JSON parameter handler
export const JsonParam: QueryParamConfig<any> = {
    encode: (value) => {
        try {
            return value != null ? JSON.stringify(value) : undefined;
        } catch {
            return undefined;
        }
    },
    decode: (value) => {
        if (Array.isArray(value)) {
            value = value[0];
        }
        if (!value) return undefined;
        try {
            return JSON.parse(value);
        } catch {
            return undefined;
        }
    },
};

// withDefault utility
export function withDefault<T>(param: QueryParamConfig<T>, defaultValue: T): QueryParamConfig<T> {
    return {
        encode: param.encode,
        decode: (value) => {
            const decoded = param.decode(value);
            return decoded != null ? decoded : defaultValue;
        },
    };
}

// Create enum parameter handlers for backward compatibility
export function createEnumParam<T extends string>(enumValues: T[]) {
    return {
        encode: (value: T | null) => value,
        decode: (value: string | (string | null)[] | null | undefined): T | null => {
            if (Array.isArray(value)) {
                value = value[0];
            }
            if (value && enumValues.includes(value as T)) {
                return value as T;
            }
            return null;
        },
    };
}

export function createEnumArrayParam<T extends string>(enumValues: T[]) {
    return {
        encode: (value: T[]) => (value.length ? value : undefined),
        decode: (value: string | (string | null)[] | null | undefined): T[] => {
            if (Array.isArray(value)) {
                return value.filter((v): v is T => v != null && enumValues.includes(v as T));
            }
            if (value && enumValues.includes(value as T)) {
                return [value as T];
            }
            return [];
        },
    };
}
